/**
 * Симетричне шифрування — XChaCha20-Poly1305, алгоритм 1 конверта.
 *
 * Це той примітив, яким шифрується все, що подорожує: вміст записів і
 * обгортки ключів. Причина вибору в [ADR-0008](../../../../docs/adr/0008-crypto-primitives.md)
 * і зводиться до нонса: 192 біти означають, що випадковий нонс безпечний
 * без жодного лічильника. У нас кілька пристроїв пишуть офлайн і не мають
 * як домовитися про лічильник — з 96-бітним нонсом це був би реальний ризик
 * повтору, а повтор нонса в AEAD віддає відкритий текст.
 *
 * Чому не WebCrypto: WebCrypto не вміє XChaCha20 взагалі. Різниця з
 * алгоритмом 3 (AES-GCM на невитягуваному ключі) описана в
 * [ADR-0016](../../../../docs/adr/0016-key-hierarchy.md) — обидва потрібні,
 * і плутати їх не можна.
 *
 * `aad` — додаткові дані, які не шифруються, але входять у мітку
 * автентичності. Ми кладемо туди призначення блоба, щоб обгортку одного
 * призначення не можна було підсунути замість іншого: підміна не
 * розшифрується замість того, щоб тихо спрацювати.
 */
import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { randomBytes, utf8ToBytes } from "@noble/ciphers/utils.js";
import { DecryptionFailedError } from "../ports/cipher.js";
import { Algorithm, decodeEnvelope, encodeEnvelope, nonceLength } from "./envelope.js";
const NONCE_BYTES = nonceLength(Algorithm.XChaCha20Poly1305);
/** Копія байтів у власний буфер — див. пояснення в `openAead`. */
function own(view) {
    const out = new Uint8Array(view.length);
    out.set(view);
    return out;
}
/** Мітки призначення для `aad`. Значення входять у шифротекст — не міняти. */
export function purpose(...parts) {
    return utf8ToBytes(["beresta", ...parts].join(":"));
}
/**
 * Шифрує в конверт алгоритму 1. Нонс випадковий на кожен виклик — саме на
 * цьому тримається безпека схеми, тож він не приймається аргументом.
 */
export function sealAead(key, plaintext, aad) {
    const nonce = randomBytes(NONCE_BYTES);
    const ciphertext = xchacha20poly1305(key, nonce, aad).encrypt(plaintext);
    return encodeEnvelope({ algorithm: Algorithm.XChaCha20Poly1305, nonce, ciphertext });
}
/**
 * Розбирає конверт і розшифровує. Кидає `DecryptionFailedError`, якщо
 * не збігається ключ, `aad` або зіпсовані байти — навмисно однаково,
 * бо Poly1305 їх не розрізняє, і вдавати інше означало б брехати.
 *
 * Помилки формату конверта (`MalformedEnvelopeError`,
 * `UnsupportedEnvelopeError`) проходять наскрізь: пошкоджене сховище — це
 * не те саме, що не той ключ, і лікується різним.
 */
export function openAead(key, sealed, aad) {
    const envelope = decodeEnvelope(sealed);
    if (envelope.algorithm !== Algorithm.XChaCha20Poly1305) {
        throw new DecryptionFailedError(`алгоритм ${envelope.algorithm} цим ключем не відкрити`);
    }
    try {
        // Копії, а не види: `decodeEnvelope` віддає підмасиви на спільний буфер
        // зі зміщенням 2, а вирівнювання під 32 біти в реалізаціях шифрів має
        // значення. Ціна — один зайвий буфер на розшифрування.
        return xchacha20poly1305(key, own(envelope.nonce), aad).decrypt(own(envelope.ciphertext));
    }
    catch {
        throw new DecryptionFailedError("не той ключ або пошкоджені дані");
    }
}
//# sourceMappingURL=aead.js.map